import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription, distinctUntilChanged } from 'rxjs';
import { AuthMethodType, AuthMethodViewModel } from '../../../../services/identity';
import { AuthMethod, DefaultChangeData, FormStatusData, MethodTypeChange, RemoveMethodData } from '../../enrollment.component';
import { EnrollmentInputModel } from '../../../enrollment/models/enrollmentinput.model';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmDialogComponent } from '../../../confirm-dialog/confirm-dialog.component';

import { NgSelectComponent } from '@ng-select/ng-select';
import { PasswordComponent } from '../password/password.component';
import { StaticCodeComponent } from '../static-code/static-code.component';
import { PasscodeEmailComponent } from '../passcode-email/passcode-email.component';
import { PasscodeSmsComponent } from '../passcode-sms/passcode-sms.component';
import { SoftTokenComponent } from '../soft-token/soft-token.component';
import { HardwareTokenComponent } from '../hardware-token/hardware-token.component';
import { YubikeyComponent } from '../yubikey/yubikey.component';
import { FidoComponent } from '../fido/fido.component';
import { MethodBase } from './method-base';

export { MethodBase } from './method-base';

@Component({
  selector: 'app-method-base',
  templateUrl: './base.component.html',
  styleUrl: './base.component.scss',
  imports: [ReactiveFormsModule, NgSelectComponent, PasswordComponent, StaticCodeComponent, PasscodeEmailComponent, PasscodeSmsComponent, SoftTokenComponent, HardwareTokenComponent, YubikeyComponent, FidoComponent, TranslatePipe]
})
export class BaseComponent implements OnInit {
  protected fb = inject(FormBuilder);
  private translate = inject(TranslateService);
  private ngbModal = inject(NgbModal);

  @Input() options!: EnrollmentInputModel;
  @Input() method: AuthMethodViewModel;
  @Input() methods: AuthMethod[];
  @Input() index: number;
  @Input() factorIndex: number;

  private originalMethod: AuthMethodViewModel;
  private myMethods: AuthMethod[];
  private selectedMethod: AuthMethod;
  private baseForm: FormGroup;

  private isEditing: boolean;
  private canRemove: boolean;
  private isSelf: boolean;
  private isDisabled: boolean;
  private allowedTemp: boolean;
  private multipleAllowed: boolean;

  @Output() typeChanged: EventEmitter<MethodTypeChange> = new EventEmitter();
  @Output() removeCalled: EventEmitter<RemoveMethodData> = new EventEmitter();
  @Output() formStatusChanged: EventEmitter<FormStatusData> = new EventEmitter();
  @Output() defaultChanged: EventEmitter<DefaultChangeData> = new EventEmitter();

  @ViewChild('methodComponent') methodComponent: MethodBase;

  ngOnInit(): void {
    if (this.method.sequence == 1) {
      this.canRemove = this.factorIndex != 0;
      this.multipleAllowed = this.options.state.allowMultiplePrimaryFactor;
      this.isSelf = this.options.user.id == 0 || this.options.currentUser.id == this.options.user.id;
      this.isDisabled = this.isSelf && this.options.state.disablePrimaryFactor;
    } else {
      this.multipleAllowed = this.options.state.allowMultipleSecondaryFactor;
      if (this.options.state.enforceSecondaryFactor) {
        this.canRemove = this.factorIndex != 0;
      } else {
        this.canRemove = true;
      }
    }

    if (this.method?.type) {
      this.selectedMethod = this.methods.find(x => x.id == this.method.type);
      this.onChooseType(this.selectedMethod);
    } else {
      this.isEditing = true;
      this.formStatusChanged.emit({ sequence: this.method.sequence, index: this.index, status: 'INVALID' });
    }
    this.baseForm = this.createBaseForm(this.method);
    this.processMethods();
    this.originalMethod = Object.assign({}, this.method); //deep clone object
    if (this.method?.type) {
      this.formStatusChanged.emit({ sequence: this.method.sequence, index: this.index, status: this.baseForm.status, touched: false });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.methods) {
      this.processMethods();
    }
  }

  processMethods() {
    this.myMethods = [];
    this.methods.forEach(val => this.myMethods.push(Object.assign({}, val))); //deep clone array of objects
    if (this.selectedMethod && this.selectedMethod.allowedForUser) {
      const method = this.myMethods.find(x => x.id == this.selectedMethod.id);
      if (method) {
        method.disabled = false;
      }
    }
  }

  private onChooseType(method: AuthMethod) {
    let change: MethodTypeChange = { previousType: this.selectedMethod?.id, newType: method?.id }
    this.selectedMethod = method;
    this.method.type = AuthMethodType[method?.id];
    this.allowedTemp = this.method.sequence == 2 && this.options.state.allowedTempTypes.indexOf(this.method.type) != -1;
    this.typeChanged.emit(change);
  }

  private onFormStatusChange(data: FormStatusData): void {
    data.index = this.index;
    this.formStatusChanged.emit(data);
  }

  private edit(): void {
    this.isEditing = true;
    this.methodComponent?.startEdit();
  }

  reset(): void {
    this.methodComponent?.endEdit();
    if (!this.originalMethod?.type) {
      this.remove();
      return;
    }
    this.isEditing = false;
    this.methodComponent?.cancel();
    this.selectedMethod = this.myMethods.find(x => x.id == this.originalMethod.type);
    this.baseForm.controls.type.setValue(this.originalMethod.type);
    this.method = Object.assign({}, this.originalMethod); //deep clone object
  }

  private remove(): void {
    this.onChooseType(null);
    let data: RemoveMethodData = { sequence: this.method.sequence, index: this.index };
    this.removeCalled.emit(data);
  }

  private createBaseForm(method: AuthMethodViewModel): FormGroup {
    return this.fb.group({
      id: [method.id],
      type: [method.type],
      sequence: [method.sequence],
      isDefault: [method.isDefault],
      isTemp: [method.revertAtTime ? true : false],
      tempRevertAt: [this.stringToFormattedDateString(method.revertAtTime)]
    });
  }

  getMethod(): AuthMethodViewModel {
    let method = this.methodComponent?.getMethod();
    method.isDefault = this.method.isDefault;
    if (!method.isDefault && this.allowedTemp && this.baseForm.value.isTemp) {
      method.revertAtTime = this.baseForm.value.tempRevertAt;
    } else {
      method.revertAtTime = null;
    }
    return method;
  }

  private setDefault(): void {
    if (this.baseForm.value.isTemp) {
      const modalRef = this.ngbModal.open(ConfirmDialogComponent, {
        size: 'md',
        windowClass: 'fadeInUp animated huge',
        backdrop: 'static',
        keyboard: false,
      });
      modalRef.componentInstance.message = this.translate.instant(
        'MyProfile.ConfirmDefaultForTemp'
      );

      modalRef.result.then((userResponse: boolean) => {
        if (!userResponse) return;
        this.baseForm.controls.isTemp.setValue(false);
        this.baseForm.controls.tempRevertAt.disable();
        this.setDefaultInternal();
      })
    } else {
      this.setDefaultInternal();
    }
  }

  private setDefaultInternal(): void {
    this.method.isDefault = true;
    this.defaultChanged.emit({ sequence: this.method.sequence, index: this.index });
  }

  removeDefault(): void {
    this.method.isDefault = false;
  }

  changeIsTemp() {
    if (this.baseForm.value.isTemp) {
      this.baseForm.controls.isTemp.setValue(false);
      this.baseForm.controls.tempRevertAt.disable();
    } else {
      this.baseForm.controls.isTemp.setValue(true);
      this.baseForm.controls.tempRevertAt.enable();
    }
    this.emitFormStatus();
  }

  emitFormStatus() {
    this.formStatusChanged.emit({ sequence: this.method.sequence, index: this.index, status: this.methodComponent?.form?.status, touched: true });
  }

  getDefaultTempDate(): string {
    let date = new Date();
    date.setDate(date.getDate() + 1);
    return this.dateToFormattedDateString(date);
  }

  dateToFormattedDateString(date: Date): string {
    return (date.getFullYear().toString() + '-'
      + ("0" + (date.getMonth() + 1)).slice(-2) + '-'
      + ("0" + (date.getDate())).slice(-2))
      + 'T' + date.toTimeString().slice(0, 5);
  }

  stringToFormattedDateString(dateString: string): string {
    if (!dateString) {
      return this.getDefaultTempDate();
    }
    const date = new Date(dateString);
    return this.dateToFormattedDateString(date);
  }

}
